import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Container, 
  Box, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { motion } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { label: 'Accueil', href: '#hero', id: 'hero' },
    { label: 'Notre approche', href: '#approach', id: 'approach' },
    { label: 'Services', href: '#services', id: 'services' },
    { label: 'Témoignages', href: '#testimonials', id: 'testimonials' },
    { label: 'Contact', href: '#contact', id: 'contact' }
  ];

  const handleNavClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    
    // Fonction de défilement fluide intégrée
    const element = document.getElementById(id);
    
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80; // 80px d'offset pour le header
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ p: 2, bgcolor: 'primary.main' }}>
        <Typography variant="h6" sx={{ color: 'white', fontFamily: '"Playfair Display", serif' }}>
          BONAVIA
        </Typography>
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton 
              sx={{ textAlign: 'center' }}
              component="a"
              href={item.href}
              onClick={(e) => handleNavClick(e as React.MouseEvent<HTMLAnchorElement>, item.id)}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <AppBar 
        position="fixed" 
        color={isScrolled ? "primary" : "transparent"}
        elevation={isScrolled ? 4 : 0}
        sx={{ 
          transition: 'all 0.3s ease',
          bgcolor: isScrolled ? 'primary.main' : 'transparent',
          boxShadow: isScrolled ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Typography
              variant="h6"
              component="a"
              href="#hero"
              onClick={(e) => handleNavClick(e, 'hero')}
              sx={{
                mr: 2,
                display: 'flex',
                fontFamily: '"Tenor Sans", sans-serif',
                fontWeight: 700,
                letterSpacing: '.1rem',
                color: isScrolled ? 'white' : 'primary.main',
                textDecoration: 'none',
                flexGrow: { xs: 1, md: 0 }
              }}
            >
              BONAVIA
              <Box 
                component="span" 
                sx={{ 
                  display: 'inline-block', 
                  width: '6px', 
                  height: '6px', 
                  borderRadius: '50%', 
                  bgcolor: 'error.main', 
                  ml: 0.5, 
                  mt: 1 
                }} 
              />
            </Typography>

            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ 
                  color: isScrolled ? 'white' : 'primary.main',
                }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                {navItems.map((item) => (
                  <Button 
                    key={item.label} 
                    href={item.href}
                    onClick={(e) => handleNavClick(e as React.MouseEvent<HTMLAnchorElement>, item.id)}
                    sx={{ 
                      color: isScrolled ? 'white' : 'primary.main',
                      mx: 1,
                      '&:hover': {
                        color: isScrolled ? 'secondary.main' : 'error.main',
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {!isMobile && (
              <Button 
                variant="contained" 
                color="error"
                href="#contact"
                onClick={(e) => handleNavClick(e as React.MouseEvent<HTMLAnchorElement>, 'contact')}
                sx={{ 
                  px: 3,
                  borderRadius: '2px',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none',
                    bgcolor: '#d32f6c',
                  }
                }}
              >
                Prendre rendez-vous
              </Button>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>
      
      {/* Spacer pour compenser la hauteur de l'AppBar */}
      <Toolbar />
    </motion.div>
  );
};

export default Header;
